import { getFutuSymbol } from '@/shared/hooks/futures/useCommon'
import { create } from 'zustand'

interface SettingBotState {
  tab: 'recommand' | 'simple' | 'detail'
  setTab: (tab: 'recommand' | 'simple' | 'detail') => void

  // 체크 모달 오픈 여부
  checkModalOpen: boolean
  setCheckModalOpen: (checkModalOpen: boolean) => void

  // 선택된 추천 전략 ID
  selectedRecommendId: string | null
  setSelectedRecommendId: (id: string | null) => void

  // 필수 설정
  required: {
    id_tactic: string // 코인
    position_side: 'LONG' | 'SHORT' // 포지션 방향
    id_ex: string // 거래소
    leverage: string // 레버리지
    margin_mode: 'ISOLATED' | 'CROSS' // 마진 모드
    tp_entry_initial: 'MARKET' | 'LIMIT' // 진입 방식
    price_initial_limit: string // 진입 가격
    pct_take_profit: string // 수익률
    funds_per_asset: string // 자산 당 투자 금액
  }

  // 햇지옵션
  hedging: {
    hedge_ratio: string
    hedge_usdt: string
  }

  // TP 설정
  tp: {
    tp_setting: 'simple' | 'detail'
    simple_setting: {
      cnt_entry: string
      rate_extra_entry: string
      gab_extra_entry: string
    } | null
    detail_setting: Array<{
      gab_extra_entry: string
      amt_extra_entry: string
      pct_tp?: string | null
    }>
    yn_use_pct_tp: 'Y' | 'N'
  }

  // 옵션
  option: {
    pct_stop_loss: string
    yn_repeat: 'Y' | 'N'
    // UI 전용: 손절 사용 여부 (API로 전송하지 않음)
    yn_use_stop_loss: 'Y' | 'N'
    yn_stop_loss: 'Y' | 'N'
    tp_entry_additional: 'MARKET' | 'LIMIT'
    rate_repeat_entry: string
    tp_repeat: 'FAST' | 'SLOW'
    // UI 전용: Min/Max Price 사용 여부 (API로 전송하지 않음)
    yn_use_threshold_price: 'Y' | 'N'
    threshold_price: string
  }

  // 액션들
  setRequired: (key: keyof SettingBotState['required'], value: string) => void
  setHedging: (key: keyof SettingBotState['hedging'], value: string) => void
  setTPSetting: (setting: 'simple' | 'detail') => void
  setSimpleSetting: (key: keyof SettingBotState['tp']['simple_setting'], value: string) => void
  setDetailSetting: (
    settings: Array<{
      gab_extra_entry: string
      amt_extra_entry: string
      pct_tp?: string | null
    }>,
  ) => void
  setOption: (key: keyof SettingBotState['option'], value: string) => void

  // Field Array 관련 메서드들
  appendDetailSetting: (item: { gab_extra_entry: string; amt_extra_entry: string; pct_tp?: string }) => void
  removeDetailSetting: (index: number) => void
  updateDetailSetting: (index: number, newItem: { gab_extra_entry: string; amt_extra_entry: string; pct_tp?: string }) => void

  // 통합 핸들러
  updateSetting: (path: string, value: string) => void

  // 기본값 리셋 (심볼/포지션에 맞춰 기본 세팅으로 초기화)
  resetDefaultsForSymbol: (idTactic: string, positionSide?: 'LONG' | 'SHORT') => void
}

export const settingBot = create<SettingBotState>((set, get) => ({
  tab: 'recommand',
  setTab: (tab) => set({ tab, selectedRecommendId: null }),
  checkModalOpen: false,
  setCheckModalOpen: (checkModalOpen) => set({ checkModalOpen }),
  selectedRecommendId: null,
  setSelectedRecommendId: (id) => set({ selectedRecommendId: id }),

  required: {
    id_tactic: getFutuSymbol().split('USDT')[0], // USDT 제거해야함
    id_ex: '6',
    position_side: 'LONG',
    leverage: '20',
    margin_mode: 'CROSS',
    tp_entry_initial: 'MARKET',
    price_initial_limit: '',
    pct_take_profit: '10',
    funds_per_asset: '',
  },

  hedging: {
    hedge_ratio: '',
    hedge_usdt: '',
  },

  tp: {
    tp_setting: 'simple',
    simple_setting: {
      cnt_entry: '5',
      rate_extra_entry: '2',
      gab_extra_entry: '2',
    },
    detail_setting: [
      {
        gab_extra_entry: '0',
        amt_extra_entry: '',
        pct_tp: '',
      },
      {
        gab_extra_entry: '',
        amt_extra_entry: '',
        pct_tp: '',
      },
      {
        gab_extra_entry: '',
        amt_extra_entry: '',
        pct_tp: '',
      },
      {
        gab_extra_entry: '',
        amt_extra_entry: '',
        pct_tp: '',
      },
      {
        gab_extra_entry: '',
        amt_extra_entry: '',
        pct_tp: '',
      },
    ],
    yn_use_pct_tp: 'N',
  },

  option: {
    pct_stop_loss: '',
    yn_repeat: 'Y',
    yn_use_stop_loss: 'N',
    yn_stop_loss: 'N',
    tp_entry_additional: 'MARKET',
    rate_repeat_entry: '',
    tp_repeat: 'FAST',
    yn_use_threshold_price: 'N',
    threshold_price: '',
  },

  setRequired: (key, value) =>
    set((state) => ({
      required: { ...state.required, [key]: value },
    })),

  setHedging: (key, value) =>
    set((state) => ({
      hedging: { ...state.hedging, [key]: value },
    })),

  setTPSetting: (setting: 'simple' | 'detail') =>
    set((state) => {
      if (setting === 'detail') {
        // 상세설정으로 변경할 때: 간편설정의 cnt_entry만큼 상세설정 배열 생성
        const cntEntry = parseInt(state.tp.simple_setting?.cnt_entry || '1')
        const detailSettings = Array.from({ length: cntEntry }, (_, index) => ({
          gab_extra_entry: index === 0 ? '0' : '',
          amt_extra_entry: '',
          pct_tp: '',
        }))

        return {
          tp: {
            ...state.tp,
            tp_setting: setting,
            detail_setting: detailSettings,
          },
        }
      } else {
        // 간편설정으로 변경할 때: 상세설정 배열 길이를 간편설정의 cnt_entry로 설정
        const detailLength = state.tp.detail_setting.length

        return {
          tp: {
            ...state.tp,
            tp_setting: setting,
            simple_setting: {
              cnt_entry: String(detailLength),
              rate_extra_entry: state.tp.simple_setting?.rate_extra_entry || '2',
              gab_extra_entry: state.tp.simple_setting?.gab_extra_entry || '2',
            },
          },
        }
      }
    }),

  setSimpleSetting: (key, value) =>
    set((state) => ({
      tp: {
        ...state.tp,
        simple_setting: {
          ...(state.tp.simple_setting as {
            cnt_entry: string
            rate_extra_entry: string
            gab_extra_entry: string
          }),
          [key]: value,
        },
      },
    })),

  setDetailSetting: (settings) =>
    set((state) => {
      // 첫 번째 gab_extra_entry가 무조건 "0"이 되도록 보장
      const validatedSettings = settings.map((setting, index) => ({
        ...setting,
        gab_extra_entry: index === 0 ? '0' : setting.gab_extra_entry,
        pct_tp: setting.pct_tp ?? null,
      }))

      return {
        tp: { ...state.tp, detail_setting: validatedSettings },
      }
    }),

  setOption: (key, value) =>
    set((state) => ({
      option: { ...state.option, [key]: value },
    })),

  // Field Array 관련 메서드들
  appendDetailSetting: (item: { gab_extra_entry: string; amt_extra_entry: string; pct_tp?: string }) =>
    set((state) => {
      const newDetailSetting =
        state.tp.detail_setting.length < 20
          ? [...state.tp.detail_setting, { ...item, pct_tp: item.pct_tp || '' }]
          : state.tp.detail_setting

      // 첫 번째 gab_extra_entry가 무조건 "0"이 되도록 보장
      const validatedDetailSetting = newDetailSetting.map((setting, index) => ({
        ...setting,
        gab_extra_entry: index === 0 ? '0' : setting.gab_extra_entry,
      }))

      return {
        tp: {
          ...state.tp,
          detail_setting: validatedDetailSetting,
          simple_setting: state.tp.simple_setting
            ? {
                ...state.tp.simple_setting,
                cnt_entry: String(validatedDetailSetting.length),
              }
            : null,
        },
      }
    }),

  removeDetailSetting: (index: number) =>
    set((state) => {
      const newDetailSetting =
        state.tp.detail_setting.length > 2
          ? state.tp.detail_setting.filter((_, i) => i !== index)
          : state.tp.detail_setting

      // 첫 번째 gab_extra_entry가 무조건 "0"이 되도록 보장
      const validatedDetailSetting = newDetailSetting.map((setting, idx) => ({
        ...setting,
        gab_extra_entry: idx === 0 ? '0' : setting.gab_extra_entry,
      }))

      return {
        tp: {
          ...state.tp,
          detail_setting: validatedDetailSetting,
          simple_setting: state.tp.simple_setting
            ? {
                ...state.tp.simple_setting,
                cnt_entry: String(validatedDetailSetting.length),
              }
            : null,
        },
      }
    }),

  updateDetailSetting: (index: number, newItem: { gab_extra_entry: string; amt_extra_entry: string; pct_tp?: string }) =>
    set((state) => {
      const updatedDetailSetting = state.tp.detail_setting.map((item, i) => {
        if (i === index) {
          // 첫 번째 항목인 경우 gab_extra_entry를 "0"으로 강제 설정
          return {
            ...newItem,
            gab_extra_entry: i === 0 ? '0' : newItem.gab_extra_entry,
            pct_tp: newItem.pct_tp ?? item.pct_tp ?? '',
          }
        }
        return item
      })

      return {
        tp: {
          ...state.tp,
          detail_setting: updatedDetailSetting,
        },
      }
    }),

  // 통합 핸들러
  updateSetting: (path: string, value: string) =>
    set((state) => {
      const pathParts = path.split('.')

      // 재귀 함수로 중첩된 객체 업데이트
      const updateNestedObject = (obj: any, parts: string[], value: string): any => {
        if (parts.length === 1) {
          return { ...obj, [parts[0]]: value }
        }

        const [currentPart, ...remainingParts] = parts

        // 배열 인덱스 처리
        const arrayMatch = currentPart.match(/^(\w+)\[(\d+)\]$/)
        if (arrayMatch) {
          const [, arrayName, indexStr] = arrayMatch
          const index = parseInt(indexStr)
          const array = obj[arrayName] || []

          if (!Array.isArray(array)) {
            console.error(`${arrayName} is not an array`)
            return obj
          }

          const updatedArray = [...array]
          if (remainingParts.length === 1) {
            // 배열 요소의 직접 속성 업데이트
            updatedArray[index] = { ...updatedArray[index], [remainingParts[0]]: value }
          } else {
            // 중첩된 객체 업데이트
            updatedArray[index] = updateNestedObject(updatedArray[index] || {}, remainingParts, value)
          }

          return { ...obj, [arrayName]: updatedArray }
        }

        // 일반 객체 속성 처리
        return {
          ...obj,
          [currentPart]: updateNestedObject(obj[currentPart] || {}, remainingParts, value),
        }
      }

      return updateNestedObject(state, pathParts, value)
    }),

  resetDefaultsForSymbol: (idTactic: string, positionSide: 'LONG' | 'SHORT' = 'LONG') =>
    set((state) => ({
      required: {
        id_tactic: idTactic,
        id_ex: '6',
        position_side: positionSide,
        // 레버리지는 심볼 변경 로직(setExSymbol)에서 보정되므로 기존 값을 유지
        leverage: state.required.leverage,
        margin_mode: 'CROSS',
        tp_entry_initial: 'MARKET',
        price_initial_limit: '',
        pct_take_profit: '10',
        funds_per_asset: '',
      },
      hedging: {
        hedge_ratio: '',
        hedge_usdt: '',
      },
      tp: {
        tp_setting: 'simple',
        simple_setting: {
          cnt_entry: '5',
          rate_extra_entry: '2',
          gab_extra_entry: '2',
        },
        // 기본 상세설정은 초기 상태와 동일하게 유지
        detail_setting: [
          { gab_extra_entry: '0', amt_extra_entry: '', pct_tp: '' },
          { gab_extra_entry: '', amt_extra_entry: '', pct_tp: '' },
          { gab_extra_entry: '', amt_extra_entry: '', pct_tp: '' },
          { gab_extra_entry: '', amt_extra_entry: '', pct_tp: '' },
          { gab_extra_entry: '', amt_extra_entry: '', pct_tp: '' },
        ],
        yn_use_pct_tp: 'N',
      },
      option: {
        pct_stop_loss: '',
        yn_repeat: 'Y',
        yn_use_stop_loss: 'N',
        yn_stop_loss: 'N',
        tp_entry_additional: 'MARKET',
        rate_repeat_entry: '',
        tp_repeat: 'FAST',
        yn_use_threshold_price: 'N',
        threshold_price: '',
      },
      // tab은 호출 측에서 제어 (예: setTab('simple'))
    })),
}))
